<template>
    <!-- The panel opens on itself: its first control is the search box, where the
         reader's arrows would move a caret instead of turning the page. -->
    <!-- Not modal while the page is being asked for a place: the page has the
         keyboard, and the reader answers Escape by leaving the capture. -->
    <SidebarPanel
        :is-open="isOnScreen"
        :modal="!addOnEditorAwaitsAnchor"
        :title="panelTitle"
        side="left"
        persistent
        mobile-fullscreen
        resizable
        :fullscreen="ui.addOnsPanel.fullscreen"
        fullscreen-footer
        focus-trap-fallback="container"
        dusk="ReaderAddOnsPanel"
        @close="onClose"
    >
        <!-- The aside speaks for the list or for the piece being read. The form puts its preview there instead. -->
        <template #title-aside>
            <span class="flex items-center gap-1.5">
                <AddOnDraftPill v-if="isDraftBeingEdited || isDraftBeingRead" />
                <button
                    v-if="addOnBeingEdited"
                    ref="previewToggle"
                    type="button"
                    dusk="AddOnsPreviewButton"
                    :class="classes.previewToggle"
                    @click="previewing ? leavePreview() : enterPreview()"
                >
                    {{ previewing ? strings.exitPreview : strings.preview }}
                </button>
                <template v-else>
                    <span v-if="addOnNavigation" dusk="AddOnsPosition" :class="classes.aside">{{ positionLabel }}</span>
                    <span v-else-if="visibleAddOns.length" dusk="AddOnsCount" :class="classes.aside">{{
                        countLabel
                    }}</span>
                </template>
            </span>
        </template>
        <template #toolbar>
            <!-- A stored piece is left through the footer, which lands on the piece, so only a new one needs a way out here. -->
            <template v-if="addOnBeingEdited">
                <div v-if="isNewAddOn && !previewing" class="flex items-center justify-between pb-2">
                    <button
                        type="button"
                        dusk="AddOnsEditorLeaveButton"
                        :class="classes.toolbarLink"
                        @click="cancelAddOnEditor()"
                    >
                        <svg-icon icon="arrow-left" width="16px" height="16px" />
                        <span>{{ strings.backToList }}</span>
                    </button>
                </div>
            </template>
            <div v-else-if="addOnBeingRead" class="flex items-center justify-end pb-2">
                <div class="flex items-center gap-1">
                    <PillIconButton
                        v-tooltip.top="strings.textSmaller"
                        dusk="AddOnsTextSmaller"
                        icon="minus"
                        :aria-label="strings.textSmaller"
                        :disabled="fontScale === smallestFontScale"
                        variant="muted"
                        touch-target
                        @click="stepFontScale(-1)"
                    />
                    <PillIconButton
                        v-tooltip.top="strings.textLarger"
                        dusk="AddOnsTextLarger"
                        icon="plus"
                        :aria-label="strings.textLarger"
                        :disabled="fontScale === largestFontScale"
                        variant="muted"
                        touch-target
                        @click="stepFontScale(1)"
                    />
                    <PillIconButton
                        v-if="isShareEnabled && !socialShareDisabled && canShareAddOnBeingRead"
                        v-tooltip.top="shareLabel"
                        dusk="AddOnsShareButton"
                        icon="share-nodes"
                        :aria-label="shareLabel"
                        variant="muted"
                        touch-target
                        @click="shareAddOn"
                    />
                    <PillIconButton
                        v-if="!onlyMobileVisible"
                        v-tooltip.top="fullscreenLabel"
                        dusk="AddOnsFullscreenToggle"
                        :icon="ui.addOnsPanel.fullscreen ? 'arrows-pointing-in-24px' : 'arrows-pointing-out-24px'"
                        :aria-label="fullscreenLabel"
                        :aria-pressed="String(ui.addOnsPanel.fullscreen)"
                        variant="muted"
                        touch-target
                        @click="toggleAddOnViewerFullscreen()"
                    />
                    <PillIconButton
                        v-if="canEditAddOns"
                        ref="editButton"
                        v-tooltip.top="strings.edit"
                        dusk="AddOnsEditButton"
                        icon="baseline-edit-24px"
                        :aria-label="strings.edit"
                        variant="muted"
                        touch-target
                        @click="editAddOn(addOnBeingRead.id)"
                    />
                </div>
            </div>
            <div
                v-else-if="visibleAddOns.length"
                class="mb-2 flex min-h-11 items-center rounded-lg border-2 border-theme-border bg-theme-bg pl-3 md:min-h-0"
            >
                <svg-icon icon="magnifying-glass" class="shrink-0 text-theme-toolbar-icon" width="20px" height="20px" />
                <!-- Stretched to the box, so a tap on a phone's taller box lands in the field, not above or below it. -->
                <input
                    dusk="AddOnsSearchInput"
                    type="search"
                    :value="ui.addOnsPanel.search"
                    :placeholder="strings.search"
                    :aria-label="strings.search"
                    class="w-full appearance-none self-stretch border-none bg-transparent py-1.5 pl-2 pr-0.5 text-sm text-theme-text outline-none selection:bg-theme-accent selection:text-theme-accent-on focus-visible:ring-2 focus-visible:ring-theme-accent"
                    @input="setAddOnSearch($event.target.value)"
                    @focus="holdArrowKeys"
                    @blur="releaseArrowKeys"
                />
            </div>
        </template>

        <!-- Editing leaves the piece open behind the form, so the form comes first.
             The preview only hides it, so leaving the preview finds the form as it was left. -->
        <template v-if="addOnBeingEdited">
            <AddOnEditor
                v-show="!previewing"
                ref="editor"
                :form-id="formId"
                @request-publish="askPublish"
                @save-failed="handBackFocus('submit')"
                @image-preview="imageFileUrl = $event"
            />
            <AddOnViewer v-if="previewing" ref="viewer" :add-on="previewAddOn" />
        </template>
        <AddOnViewer v-else-if="addOnBeingRead" ref="viewer" :add-on="addOnBeingRead" />
        <AddOnList v-else ref="list" />

        <template v-if="footerMode" #footer>
            <template v-if="footerMode === 'editor'">
                <p
                    v-if="addOnBeingEdited.deleteError"
                    role="alert"
                    dusk="AddOnsDeleteError"
                    class="m-0 mb-2 w-full rounded-lg bg-theme-danger-bg px-3 py-2 text-[12.5px] text-theme-danger"
                >
                    {{ strings.deleteFailed }}
                </p>
                <div v-if="confirmingDelete" class="flex w-full flex-col gap-2">
                    <p role="alert" class="m-0 text-[12.5px] text-theme-text">{{ deleteConfirmText }}</p>
                    <div class="flex justify-end gap-2">
                        <button
                            ref="cancelDelete"
                            type="button"
                            dusk="AddOnsDeleteCancelButton"
                            :class="classes.secondary"
                            @click="cancelDelete"
                        >
                            {{ strings.cancel }}
                        </button>
                        <!-- Enter in the title can still send a save while the question is up. -->
                        <button
                            type="button"
                            dusk="AddOnsDeleteConfirmButton"
                            :class="classes.danger"
                            :disabled="addOnBeingEdited.busy"
                            @click="confirmDelete"
                        >
                            {{ strings.delete }}
                        </button>
                    </div>
                </div>
                <div v-else-if="confirmingPublish" class="flex w-full flex-col gap-2">
                    <p role="alert" class="m-0 text-[12.5px] text-theme-text">{{ publishConfirmText }}</p>
                    <div class="flex justify-end gap-2">
                        <button
                            ref="declinePublish"
                            type="button"
                            dusk="AddOnsPublishCancelButton"
                            :class="classes.secondary"
                            @click="declinePublish"
                        >
                            {{ declinePublishLabel }}
                        </button>
                        <button
                            type="button"
                            dusk="AddOnsPublishConfirmButton"
                            :class="classes.primary"
                            :disabled="addOnBeingEdited.busy"
                            @click="confirmPublish"
                        >
                            {{ strings.publish }}
                        </button>
                    </div>
                </div>
                <div v-else class="flex w-full items-center gap-2">
                    <!-- Until the host answers the last save or delete, the form asks for no other. -->
                    <button
                        v-if="!isNewAddOn"
                        ref="delete"
                        type="button"
                        dusk="AddOnsDeleteButton"
                        :class="classes.deleteLink"
                        :disabled="addOnBeingEdited.busy"
                        @click="askDelete"
                    >
                        <svg-icon icon="trash" width="14px" height="14px" />
                        <span>{{ strings.delete }}</span>
                    </button>
                    <!-- A draft saves every change as it is made, so it has nothing to cancel. -->
                    <button
                        v-if="addOnEditorAutosaves"
                        type="button"
                        dusk="AddOnsBackButton"
                        :class="[classes.secondary, 'ml-auto']"
                        @click="leaveDraft"
                    >
                        {{ strings.back }}
                    </button>
                    <button
                        v-else
                        type="button"
                        dusk="AddOnsCancelButton"
                        :class="[classes.secondary, 'ml-auto']"
                        @click="cancelAddOnEditor()"
                    >
                        {{ strings.cancel }}
                    </button>
                    <!-- Until the page answers a capture, the piece has nowhere to go. Until the host
                         answers a save, a second press would store it twice. -->
                    <button
                        v-if="isNewAddOn"
                        ref="saveDraft"
                        type="button"
                        dusk="AddOnsSaveDraftButton"
                        :class="classes.secondary"
                        :disabled="addOnEditorAwaitsAnchor || addOnBeingEdited.busy"
                        @click="saveAsDraft"
                    >
                        {{ strings.saveDraft }}
                    </button>
                    <button
                        ref="submit"
                        type="submit"
                        :form="formId"
                        dusk="AddOnsSaveButton"
                        :class="classes.primary"
                        :disabled="addOnEditorAwaitsAnchor || addOnBeingEdited.busy"
                    >
                        {{ saveLabel }}
                    </button>
                </div>
            </template>
            <!-- Each button renders only when its neighbour exists. The empty span keeps the other on its side. -->
            <nav
                v-else-if="footerMode === 'viewer'"
                dusk="AddOnsNav"
                class="flex w-full items-center justify-between gap-2"
                :class="{ 'mx-auto max-w-[68ch]': ui.addOnsPanel.fullscreen }"
                :aria-label="strings.title"
            >
                <button
                    v-if="addOnNavigation.previous"
                    type="button"
                    dusk="AddOnsPreviousButton"
                    :class="classes.navButton"
                    @click="goToAddOn(addOnNavigation.previous.id)"
                >
                    <svg-icon icon="chevron-left" width="18px" height="18px" />
                    <span>{{ strings.previous }}</span>
                </button>
                <span v-else />
                <button
                    v-if="addOnNavigation.next"
                    type="button"
                    dusk="AddOnsNextButton"
                    :class="classes.navButton"
                    @click="goToAddOn(addOnNavigation.next.id)"
                >
                    <span>{{ strings.next }}</span>
                    <svg-icon icon="chevron-right" width="18px" height="18px" />
                </button>
                <span v-else />
            </nav>
            <div v-else class="flex w-full flex-col gap-2">
                <AddOnBatchStatus @dismissed="focusAfterBatch" />
                <!-- Equal side columns keep the create button centered, with or without the import. -->
                <div class="grid w-full grid-cols-[1fr_auto_1fr] items-center gap-2">
                    <!-- Square at the pill's height. The pill's px-4 comes later in the stylesheet than px-0. -->
                    <button
                        key="tour"
                        type="button"
                        dusk="AddOnsTourButton"
                        :class="[classes.secondary, 'w-11 justify-center justify-self-start !px-0 md:w-9']"
                        :aria-label="tourStrings.replayAriaLabel"
                        :title="tourStrings.replayAriaLabel"
                        @click="startTour('addons-editor')"
                    >
                        <span aria-hidden="true">?</span>
                    </button>
                    <!-- Keyed so the form's footer never reuses it: the click that opens the form
                     would then submit it, and the new button's color would fade from the pill's. -->
                    <button
                        key="create"
                        ref="create"
                        type="button"
                        dusk="AddOnsCreateButton"
                        :class="classes.primary"
                        @click="createAddOn()"
                    >
                        <svg-icon icon="plus" width="16px" height="16px" />
                        <span>{{ strings.create }}</span>
                    </button>
                    <button
                        v-if="canBatchAddOns"
                        key="batch"
                        ref="batch"
                        type="button"
                        dusk="AddOnsBatchButton"
                        aria-haspopup="dialog"
                        :class="[classes.secondary, 'justify-self-end']"
                        :disabled="addOnBatchRunning"
                        :title="strings.batchTitle"
                        @click="openAddOnBatchDialog"
                    >
                        {{ strings.batchOpen }}
                    </button>
                </div>
            </div>
        </template>
    </SidebarPanel>
</template>

<script>
'use strict';

import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import AddOnBatchStatus from './AddOnBatchStatus.vue';
import AddOnDraftPill from './AddOnDraftPill.vue';
import AddOnEditor from './AddOnEditor.vue';
import AddOnList from './AddOnList.vue';
import AddOnViewer from './AddOnViewer.vue';
import PillIconButton from '../Components/PillIconButton.vue';
import { PILL_CLASSES } from '../Components/buttons/pillClasses';
import SidebarPanel from '../Components/SidebarPanel.vue';
import { addOnStrings } from './addOnStrings.js';
import { tourStrings } from '../../../src/js/tours/tourStrings';
import { ADD_ON_FONT_SCALES } from './addOnFontScale';
import { ADD_ON_DRAFT, ADD_ON_PUBLISHED, isAddOnDraft, isLocalAddOnId } from './addOnModel';
import { recordFromDraft } from './addOnDraft';
import { announceToScreenReader } from '../../js/utils/screenReaderAnnounce.js';
import { addOnLink } from '../../js/utils/addOnLink';
import { uiAPI } from '../../../src/js/communication';
import { pushBackStep } from '../../js/utils/backSteps';

// The panel's own styling, kept out of the template so a control's markup
// shows what it does rather than how it is painted.
const CLASSES = {
    // The header's aside, prototype-sized: the count over the list, the position over a piece.
    aside: 'shrink-0 text-[11.5px] font-medium text-theme-toolbar-icon',
    // The prototype's footer buttons: text-sized, quiet until hovered.
    navButton:
        'inline-flex h-11 md:h-[26px] items-center gap-0.5 rounded-md px-2 text-[12.5px] font-medium text-theme-text transition-colors hover:bg-theme-accent-bg hover:text-theme-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent',
    // The form's look at the piece as a reader would open it, and the way back.
    previewToggle:
        'inline-flex h-11 md:h-7 shrink-0 items-center whitespace-nowrap rounded-md px-2 text-[12.5px] font-medium text-theme-accent transition-colors hover:bg-theme-accent-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent',
    // The toolbar's way out of a new piece or of the piece being read.
    toolbarLink:
        'inline-flex min-h-11 md:min-h-0 items-center gap-1 rounded-md px-1.5 py-1 text-[13px] font-medium text-theme-toolbar-icon transition-colors hover:bg-theme-accent-bg hover:text-theme-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent',
    primary: PILL_CLASSES.primary,
    secondary: PILL_CLASSES.secondary,
    danger: 'inline-flex h-11 md:h-9 items-center rounded-full bg-theme-danger-fill px-4 text-[12.5px] font-medium text-theme-danger-on disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent focus-visible:ring-offset-2',
    // The preview toggle in red. It shares the pills' bottom edge, so it takes their height on a phone.
    deleteLink:
        'inline-flex h-11 md:h-7 shrink-0 items-center gap-1 whitespace-nowrap rounded-md px-2 text-[12.5px] font-medium text-theme-danger transition-colors hover:bg-theme-danger-bg disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent',
};

// The form renders in the panel's body and its Save button in the footer slot,
// two separate subtrees, so the button reaches the form by id.
const FORM_ID = 'add-on-editor-form';

// How much of the piece rides along in the share blurb.
const SHARE_EXCERPT_LENGTH = 90;

export default {
    name: 'ReaderAddOnsPanel',
    components: {
        AddOnBatchStatus,
        AddOnDraftPill,
        AddOnEditor,
        AddOnList,
        AddOnViewer,
        PillIconButton,
        SidebarPanel,
    },
    data() {
        return {
            holdsArrowKeys: false,
            // The reader's other destructive actions ask through ConfirmationModal.
            // This one asks in the footer instead, because the panel is
            // already a fullscreen sheet on a phone and a modal
            // over it would stack a second one over the form.
            confirmingDelete: false,
            // Publishing asks the same way, and for the same reason.
            confirmingPublish: false,
            previewing: false,
            // Where the form was scrolled when the preview took its place.
            formScrollTop: 0,
            // The form's object URL for the file it holds, which the preview shows too.
            imageFileUrl: null,
        };
    },
    computed: {
        ...mapState(['ui', 'localizations', 'lang', 'issue', 'currentHighlight']),
        ...mapGetters([
            'visibleAddOns',
            'addOnBeingRead',
            'addOnBeingEdited',
            'shouldAddOnEditorPublish',
            'addOnNavigation',
            'canEditAddOns',
            'onlyMobileVisible',
            'canUseNativeShare',
            'isShareEnabled',
            'socialShareDisabled',
            'addOnEditorAwaitsAnchor',
            'addOnEditorType',
            'addOnEditorAutosaves',
            'ownsAddOnForm',
            'canBatchAddOns',
            'addOnBatchRunning',
        ]),
        strings() {
            return addOnStrings(this.localizations, this.lang);
        },
        tourStrings() {
            return tourStrings(this.localizations, this.lang).addOnsEditor ?? {};
        },
        classes() {
            return CLASSES;
        },
        formId() {
            return FORM_ID;
        },
        /**
         * Beside the page while the page is being asked for a place, so the
         * operator sees the anchor land in the form. A phone's panel is a
         * sheet over that page, so there it steps aside until the page
         * answers, closed rather than unmounted so the form comes back as it was.
         */
        isOnScreen() {
            return this.ui.addOnsPanel.opened && !(this.addOnEditorAwaitsAnchor && this.onlyMobileVisible);
        },
        /** The form's own heading while it is open: the piece being written, or what it is for. */
        panelTitle() {
            if (!this.addOnBeingEdited) return this.strings.title;
            if (this.isNewAddOn) return this.strings.create;

            return this.addOnBeingEdited.draft.title || this.strings.edit;
        },
        /**
         * Which footer the panel is showing, or none: the form's own buttons,
         * the neighbours of the piece being read, or the way to add a piece.
         * A preview shows none, since leaving it is all it offers.
         * @returns {'editor'|'viewer'|'list'|null}
         */
        footerMode() {
            if (this.addOnBeingEdited) return this.previewing ? null : 'editor';
            if (this.addOnNavigation) return 'viewer';

            return this.canEditAddOns && !this.addOnBeingRead ? 'list' : null;
        },
        /** The piece as the form would save it, which is what a reader would open. */
        previewAddOn() {
            const { draft, id } = this.addOnBeingEdited;
            const record = recordFromDraft(draft, id, this.addOnEditorType);

            return this.imageFileUrl ? { ...record, imageUrl: this.imageFileUrl } : record;
        },
        /** A piece the host has never stored: it publishes or waits as a draft, and has nothing to delete. */
        isNewAddOn() {
            return this.addOnBeingEdited?.id == null;
        },
        /** The piece the form holds is one an editor has not published yet. */
        isDraftBeingEdited() {
            return isAddOnDraft(this.addOnBeingEdited?.draft);
        },
        /** The piece the panel is reading is one an editor has not published yet. */
        isDraftBeingRead() {
            return isAddOnDraft(this.addOnBeingRead);
        },
        /** A link to a draft opens nothing for a reader, and a local piece exists nowhere else. */
        canShareAddOnBeingRead() {
            return !this.isDraftBeingRead && !isLocalAddOnId(this.addOnBeingRead?.id);
        },
        saveLabel() {
            return this.shouldAddOnEditorPublish ? this.strings.publish : this.strings.save;
        },
        // A new piece has no draft to keep, so declining only goes back to the form.
        declinePublishLabel() {
            return this.isNewAddOn ? this.strings.cancel : this.strings.keepDraft;
        },
        publishConfirmText() {
            return (this.strings.publishConfirm ?? '').replace('{title}', this.addOnBeingEdited.draft.title.trim());
        },
        deleteConfirmText() {
            const title = this.addOnBeingEdited.draft.title || this.strings.untitled;

            return (this.strings.deleteConfirm ?? '').replace('{title}', title);
        },
        countLabel() {
            return `${this.visibleAddOns.length} ${this.strings.title}`;
        },
        positionLabel() {
            const { position, total } = this.addOnNavigation;
            return (this.strings.position ?? '')
                .replace('{current}', String(position))
                .replace('{total}', String(total));
        },
        fullscreenLabel() {
            const { tooltips } = this.localizations[this.lang];
            return this.ui.addOnsPanel.fullscreen ? tooltips.closeFullscreen : tooltips.openFullscreen;
        },
        shareLabel() {
            return this.localizations[this.lang].tooltips.share;
        },
        fontScale() {
            return this.ui.addOnsPanel.fontScale;
        },
        searchOnScreen() {
            return this.ui.addOnsPanel.opened && !this.addOnBeingRead && this.visibleAddOns.length > 0;
        },
        smallestFontScale() {
            return ADD_ON_FONT_SCALES[0];
        },
        largestFontScale() {
            return ADD_ON_FONT_SCALES[ADD_ON_FONT_SCALES.length - 1];
        },
    },
    watch: {
        // A piece opening over the list, or the panel closing, takes the box away
        // with the focus still in it, and no blur is sure to follow.
        searchOnScreen(onScreen) {
            if (!onScreen) this.releaseArrowKeys();
        },
        // The panel is hidden, not unmounted, so a player left loaded would
        // go on sounding behind a reader who cannot reach its controls.
        'ui.addOnsPanel.opened'(opened) {
            if (opened) return;

            this.$refs.viewer?.releaseEmbeds?.();
            // The panel always reopens on its list, so a form it hid has no way
            // back, and left open it would go on pulsing on the page and holding
            // page turns and the selection tools.
            if (this.addOnBeingEdited) this.cancelAddOnEditor();
        },
        // The questions and the preview belong to one form, so none outlives it.
        addOnBeingEdited(editor) {
            this.confirmingDelete = false;
            this.confirmingPublish = false;
            this.previewing = false;
            // The pressed button goes with the form, so a panel still open hands the focus to the way to add another.
            if (!editor && !this.addOnBeingRead && this.ui.addOnsPanel.opened) {
                this.$nextTick(() => this.$refs.create?.focus());
            }
        },
        /**
         * A form waiting for a passage takes the page as its input: what the
         * reader selects is where the piece goes. A form that is waiting for
         * nothing leaves the selection to the reader, who is reading.
         */
        currentHighlight() {
            if (this.addOnEditorAwaitsAnchor) this.anchorAddOnToSelection();
        },
        // Back leaves only the capture, as Escape does, so the form keeps what was typed.
        addOnEditorAwaitsAnchor(awaitsAnchor) {
            if (awaitsAnchor) this.leaveCaptureBackStep = pushBackStep(this.cancelAddOnEditorAnchorCapture);
            else this.leaveCaptureBackStep?.();
        },
    },
    mounted() {
        this.$bus.$on('addOns:leavePage', this.onLeavePage);
        // Here rather than in an immediate watcher, so the step lands above the panel's own.
        if (this.addOnEditorAwaitsAnchor) {
            this.leaveCaptureBackStep = pushBackStep(this.cancelAddOnEditorAnchorCapture);
        }
    },
    beforeDestroy() {
        this.leaveCaptureBackStep?.();
        this.$bus.$off('addOns:leavePage', this.onLeavePage);
        // A turn whose page never arrives leaves the one-shot below listening.
        this.$bus.$off('pageChanged', this.onPageLanded);
        // Leaving it paused would cost the reader their arrow keys for good.
        this.releaseArrowKeys();
    },
    methods: {
        ...mapMutations([
            'setAddOnSearch',
            'pauseKeyboardNavigation',
            'resumeKeyboardNavigation',
            'openSocialShare',
            'setAddOnEditorTitleError',
        ]),
        ...mapActions([
            'toggleAddOnsPanel',
            'closeAddOnViewer',
            'toggleAddOnViewerFullscreen',
            'stepAddOnFontScale',
            'goToAddOn',
            'createAddOn',
            'editAddOn',
            'saveAddOnEditor',
            'cancelAddOnEditor',
            'leaveAddOnDraft',
            'deleteAddOnFromEditor',
            'anchorAddOnToSelection',
            'leavePageWithAddOnEditor',
            'anchorAddOnEditorToCurrentPage',
            'cancelAddOnEditorAnchorCapture',
            'startTour',
            'openAddOnBatchDialog',
        ]),
        focusAfterBatch() {
            this.$nextTick(() => (this.$refs.batch ?? this.$refs.create)?.focus());
        },
        /**
         * The confirmation modal only announces the answer, so the turn it held
         * back is replayed from here. The draft follows it, and a turn can land
         * after the dispatch returns (an EPUB navigator does), so the draft is
         * put on the page again once the reader reports where it ended.
         */
        onLeavePage(navigation) {
            // One answer per turn, and a turn that lands on the same screen
            // reports none, so the previous one is dropped before listening.
            this.$bus.$off('pageChanged', this.onPageLanded);
            this.$bus.$on('pageChanged', this.onPageLanded);
            this.leavePageWithAddOnEditor(navigation);
        },
        onPageLanded() {
            this.$bus.$off('pageChanged', this.onPageLanded);
            this.anchorAddOnEditorToCurrentPage();
        },
        // The panel's close button, Escape and back all land here, and each walks out one
        // level: an open question, the preview, the form, the piece being read, the panel.
        onClose() {
            if (this.confirmingDelete) this.cancelDelete();
            else if (this.confirmingPublish) this.declinePublish();
            else if (this.previewing) this.leavePreview();
            else if (this.addOnEditorAutosaves) this.leaveDraft();
            else if (this.addOnBeingEdited) this.cancelAddOnEditor();
            else if (this.addOnBeingRead) this.backToList();
            else this.toggleAddOnsPanel();
        },
        // The pressed button goes with the form, so focus lands on the pencil
        // that opens it again, or on the field that kept the draft from saving.
        async leaveDraft() {
            if (await this.leaveAddOnDraft()) {
                this.$nextTick(() => this.$refs.editButton?.$el.focus());
                return;
            }

            if (this.addOnBeingEdited?.titleError) this.$refs.editor?.focusTitle();
            else if (this.addOnBeingEdited?.imageError) this.$refs.editor?.focusImageFile();
        },
        enterPreview() {
            this.formScrollTop = this.contentBox()?.scrollTop ?? 0;
            this.previewing = true;
        },
        // Back where the operator came in: the form scrolled as it was, and
        // focus on the control that opened the preview.
        async leavePreview() {
            this.previewing = false;
            await this.$nextTick();

            const contentBox = this.contentBox();
            if (contentBox) contentBox.scrollTop = this.formScrollTop;
            this.$refs.previewToggle?.focus();
        },
        // The panel's content box is what scrolls, and the form is its child.
        contentBox() {
            return this.$refs.editor?.$el.parentElement ?? null;
        },
        // The pressed button unmounts with the question it raises, so focus
        // would otherwise fall to the document beside an unread question.
        askDelete() {
            this.confirmingDelete = true;
            this.$nextTick(() => this.$refs.cancelDelete?.focus());
        },
        // The answer unmounts with the question, so focus goes back to the Delete that raised it.
        cancelDelete() {
            this.confirmingDelete = false;
            this.handBackFocus('delete');
        },
        async confirmDelete() {
            const editor = this.addOnBeingEdited;
            this.confirmingDelete = false;
            const deleted = await this.deleteAddOnFromEditor();
            // Refused, the form is left as declining leaves it, so a retry starts from Delete.
            // A form the operator moved to while the host answered is left alone.
            if (!deleted && this.ownsAddOnForm(editor)) this.cancelDelete();
        },
        /**
         * The reader's key handler is on the document and has no input guard, so
         * an arrow typed in the search box would also turn the page. The pause is
         * one switch every feature shares, so the box gives back only what it took.
         */
        holdArrowKeys() {
            this.holdsArrowKeys = true;
            this.pauseKeyboardNavigation('addOnsPanel');
        },
        releaseArrowKeys() {
            if (!this.holdsArrowKeys) return;

            this.holdsArrowKeys = false;
            this.resumeKeyboardNavigation('addOnsPanel');
        },
        /**
         * A piece asks before it goes before every reader, which nothing in the
         * form takes back. One the save would refuse is refused first: nothing
         * while the page is being asked for the anchor, and a missing title
         * points at itself. The question replaces the buttons, so its safe
         * answer takes the focus.
         */
        askPublish() {
            if (this.addOnEditorAwaitsAnchor) return;
            if (!this.addOnBeingEdited.draft.title.trim()) {
                this.setAddOnEditorTitleError(true);
                this.$refs.editor?.focusTitle();
                return;
            }

            this.confirmingDelete = false;
            this.confirmingPublish = true;
            this.$nextTick(() => this.$refs.declinePublish?.focus());
        },
        // The buttons come back, so focus goes back to the one that asked.
        declinePublish() {
            this.confirmingPublish = false;
            this.handBackFocus('submit');
        },
        // The answer goes with the question, so a refusal hands the focus back to
        // the Publish that opened it, or to the picker whose file could not be read.
        async confirmPublish() {
            const editor = this.addOnBeingEdited;
            this.confirmingPublish = false;
            const saved = await this.saveAddOnEditor({ status: ADD_ON_PUBLISHED });
            if (!saved) this.handBackFocusOnRefusal(editor, 'submit');
        },
        // Saved, the form gives way to the piece. Refused, it points at the title it
        // needs or the file it could not read, or hands the focus back to the button
        // when the host refused it.
        async saveAsDraft() {
            const editor = this.addOnBeingEdited;
            if (await this.saveAddOnEditor({ status: ADD_ON_DRAFT })) return;

            if (this.addOnBeingEdited?.titleError) this.$refs.editor?.focusTitle();
            else this.handBackFocusOnRefusal(editor, 'saveDraft');
        },
        // The focus waits for the render that brings the button back or enables it again.
        handBackFocus(button) {
            this.$nextTick(() => this.$refs[button]?.focus());
        },
        /**
         * A save holds the button that asked for it while it travels, and a
         * disabled button drops the focus to the page, so a save the host
         * refused hands it back, where a retry starts. A file the browser
         * could not read sends it to the picker instead. A form the operator
         * moved to while the host answered is left alone.
         */
        handBackFocusOnRefusal(editor, button) {
            if (!this.ownsAddOnForm(editor)) return;

            if (editor.imageError) this.$refs.editor?.focusImageFile();
            else if (editor.saveError) this.handBackFocus(button);
        },
        /**
         * Shares a link that reopens this piece — `?add_on_id=` is the half
         * the reader already answers on load. The native sheet where the host
         * offers one, the in-app modal otherwise.
         */
        shareAddOn() {
            const addOn = this.addOnBeingRead;
            if (!addOn) return;

            if (this.canUseNativeShare) {
                uiAPI.shareContent({
                    title: addOn.title || '',
                    text: this.shareText(addOn),
                    url: addOnLink(this.issue.issue_url, addOn.id),
                });
                return;
            }

            this.openSocialShare({ shareArticle: true, currentArticleId: addOn.id });
        },
        /** The reading blurb: the opener plus the start of the piece itself. */
        shareText(addOn) {
            const { imReading } = this.localizations[this.lang];
            if (!addOn.body) return imReading;

            const element = document.createElement('div');
            element.innerHTML = addOn.body;
            const text = (element.textContent || '').substring(0, SHARE_EXCERPT_LENGTH);
            return `${imReading} "${text}..."`;
        },
        stepFontScale(direction) {
            this.stepAddOnFontScale(direction);
            announceToScreenReader(
                this.strings.textSize.replace('{percent}', String(Math.round(this.fontScale * 100)))
            );
        },
        // The focus inside the viewer unmounts with it, so it would otherwise fall to the document.
        backToList() {
            this.closeAddOnViewer();
            this.$nextTick(() => this.$refs.list?.focusFirstRow());
        },
    },
};
</script>
